import React, { useState } from 'react';
import { useNavigate,Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/authApi';
import logo from '../assets/logo.png';
import '../styles/login.css';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Call the backend API
      const { data, error: apiError } = await authApi.login(
        formData.username,
        formData.password
      );

      if (apiError || !data) {
        setError(apiError || 'Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }

      // Store user data and token
      login({
        username: data.user.username,
        name: data.user.username,
        id: data.user.id,
        user_id_guid: data.user.user_id_guid,
        session_id: data.user.session_id,
        role: data.user.role,
        menuAccess: data.user.menuAccess,
      }, data.access_token);

      // Redirect to monitoring page
      navigate('/');
    } catch (err) {
      setError('An error occurred during login. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo and Title */}
        <div className="login-header">
          <div className="login-logo-wrapper">
            <img src={logo} alt="Virmatics" className="login-logo" />
          </div>
          <p className="login-subtitle">
            Temperature Monitoring System
          </p>
        </div>

        {/* Login Card */}
        <div className="login-card">
          <h2 className="login-card__title">
            Sign In
          </h2>

          {error && (
            <div className="login-error">
              <p className="login-error__content">
                <svg className="login-error__icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Username Field */}
            <div className="login-form__field">
              <label htmlFor="username" className="login-form__label">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="login-form__input"
                placeholder="Enter your username"
                autoComplete="username"
              />
            </div>

            {/* Password Field */}
            <div className="login-form__field">
              <label htmlFor="password" className="login-form__label">
                Password
              </label>
              <div className="login-form__input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="login-form__input login-form__input--with-icon"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-form__icon-btn"
                >
                  {showPassword ? (
                    <svg className="login-form__icon" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="login-form__icon" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="login-submit-btn"
            >
              {loading ? (
                <>
                  <div className="login-submit-btn__spinner"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="login-help">
            <p className="login-info-banner__text">
              Don't have login credentials?<br />
              <span>Contact your system administrator</span>
            </p>
          </div>

          {/* Info Banner */}
          <div className="login-info-banner">
            <svg className="login-info-banner__icon" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="login-info-banner__content">
              <p className="login-info-banner__title">
                Authentication Required
              </p>
              <p className="login-info-banner__text">
                Please use your authorized credentials to access the system
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="login-footer">
          © 2026 Virmatics. All rights reserved.
          <br />
          <a
            href="/privacy-policy-en.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#64748b', textDecoration: 'underline', marginTop: '8px', display: 'inline-block' }}
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;

